import { useEffect, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FolderState, Workspace } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { BoundedApiError } from '@bounded/api-client';
import { ErrorNotice } from '@bounded/ui/state';
import { Button, Empty, Facts, Field, Input, Page, PageHeader, Panel, Skeleton } from '@bounded/ui/primitives';
import { useFormat, useT } from '../../i18n';
import { ID, slug } from '../../lib/ids';

export const Route = createFileRoute('/_app/workspaces/$workspaceId')({ component: WorkspacePage });

/**
 * Where an agent's link lands: a folder on one of your devices that an agent started working in. Choosing a
 * project here is the only way a folder gets one; the agent itself can't. Linking sends the project's latest
 * release to the device if it doesn't run that project yet.
 */
function WorkspacePage() {
  const t = useT(); const m = t.workspace;
  const { workspaceId } = Route.useParams();
  const workspace = useQuery(q.workspace(workspaceId));
  if (workspace.error instanceof BoundedApiError && workspace.error.status === 404) return <Page><Empty title={m.notFound}>{m.notFoundBody}</Empty></Page>;
  if (workspace.error) return <Page><ErrorNotice error={workspace.error} onRetry={() => void workspace.refetch()} /></Page>;
  if (!workspace.data) return <Page><Skeleton className="h-96" /></Page>;
  // Keyed by folder only: a decision changes the revision, and the page must keep showing what it just did.
  return <Folder key={workspace.data.id} w={workspace.data} />;
}

function Folder({ w }: { w: Workspace }) {
  const t = useT(); const m = t.workspace; const f = useFormat();
  const queryClient = useQueryClient();
  const projects = useQuery(q.projects());
  const items = projects.data?.items ?? [];
  const [choice, setChoice] = useState<string>(w.project_id ?? w.suggested_project_id ?? '');
  // Projects arrive after the folder: start from the first one when nothing better is known.
  const firstProject = items[0]?.id;
  useEffect(() => { if (!choice && firstProject) setChoice(firstProject); }, [choice, firstProject]);
  const [name, setName] = useState('');
  const creating = choice === '__new';
  const newId = slug(name);
  const decide = useMutation({
    mutationFn: async (next: { state: FolderState; project_id?: string }) => {
      if (next.state === 'linked' && creating) await api.saveProject(newId, name.trim());
      return api.decideWorkspace(w.id, { expected_revision: w.revision, ...next });
    },
    onSuccess: async () => { await Promise.all(['workspaces', 'projects', 'overview'].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))); },
  });
  const done = decide.data;
  const projectName = (id: string | null) => items.find((p) => p.id === id)?.name ?? id ?? '';
  const ordered = [...items].sort((a, b) => Number(b.id === w.suggested_project_id) - Number(a.id === w.suggested_project_id));
  const canLink = creating ? !!name.trim() && ID.test(newId) : !!choice && choice !== w.project_id;

  return (
    <Page>
      <PageHeader title={w.state === 'pending' ? m.chooseTitle : m.folderTitle}
        description={w.state === 'pending' ? m.pendingBody(w.device_name) : w.state === 'linked' ? m.linkedBody(projectName(w.project_id)) : m.unprotectedBody} />
      {done && (
        <div role="status" className="grid gap-1 rounded-[var(--radius-control)] border border-allow/40 bg-allow/5 px-3 py-2.5">
          <p className="font-medium">{done.state === 'linked' ? m.linked(projectName(done.project_id)) : done.state === 'unprotected' ? m.unprotected : m.reset}</p>
          {done.state === 'linked' && done.rollout === 'started' && <p className="text-sm text-graphite">{m.rolloutStarted(w.device_name)}</p>}
          {done.state === 'linked' && done.rollout === 'no_release' && <p className="text-sm text-graphite">{m.noRelease} <Link to="/projects/$projectId/policy" params={{ projectId: done.project_id! }} className="text-boundary hover:underline">{m.openPolicy}</Link></p>}
          {done.state !== 'pending' && <p className="text-sm text-graphite">{m.retry}</p>}
        </div>
      )}
      <Panel title={m.details}>
        <div className="p-4">
          <Facts items={[
            [m.folder, <code className="[overflow-wrap:anywhere]">{w.path}</code>],
            [m.device, <Link to="/devices/$deviceId" params={{ deviceId: w.device_id }} className="hover:underline">{w.device_name}</Link>],
            [m.repository, w.git_remote ? <code className="[overflow-wrap:anywhere]">{w.git_remote}</code> : <span className="text-graphite">{m.noRepository}</span>],
            [m.agent, w.host === 'claude' ? 'Claude Code' : 'Codex'],
            [m.seen, f.ago(w.created_at, Date.now())],
          ]} />
        </div>
      </Panel>
      <Panel title={w.state === 'linked' ? m.changeTitle : m.projectTitle} description={m.projectDescription}>
        <fieldset className="grid gap-1 p-4" disabled={decide.isPending}>
          <legend className="sr-only">{m.projectTitle}</legend>
          {projects.isPending && <Skeleton className="h-16" />}
          {ordered.map((p) => (
            <label key={p.id} className="flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-1.5 hover:bg-sunken">
              <input type="radio" name="project" className="size-4 accent-[var(--boundary)]" checked={choice === p.id} onChange={() => setChoice(p.id)} />
              <span className="min-w-0"><span className="font-medium">{p.name}</span> <code className="text-sm text-graphite">{p.id}</code>
                {p.id === w.suggested_project_id && <span className="ml-2 rounded bg-boundary/10 px-1.5 text-xs text-boundary">{m.suggested}</span>}
                {p.id === w.project_id && <span className="ml-2 text-sm text-graphite">{m.current}</span>}</span>
            </label>
          ))}
          <label className="flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-1.5 hover:bg-sunken">
            <input type="radio" name="project" className="size-4 accent-[var(--boundary)]" checked={creating} onChange={() => setChoice('__new')} />
            <span>{m.newProject}</span>
          </label>
          {creating && (
            <div className="ml-9 max-w-md">
              <Field label={m.newName} htmlFor="new-project" hint={newId ? <>{m.newId} <code>{newId}</code></> : undefined}>
                <Input id="new-project" autoFocus maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder={w.path.split('/').filter(Boolean).at(-1)} />
              </Field>
            </div>
          )}
          <div className="mt-3"><Button variant="primary" disabled={!canLink || decide.isPending} onClick={() => decide.mutate({ state: 'linked', project_id: creating ? newId : choice })}>
            {w.state === 'linked' ? m.change : m.link}</Button></div>
        </fieldset>
      </Panel>
      <Panel title={w.state === 'unprotected' ? m.protectTitle : m.skipTitle}>
        <div className="grid justify-items-start gap-3 p-4">
          <p className="max-w-[72ch] text-graphite">{w.state === 'unprotected' ? m.protectBody : m.skipBody}</p>
          <Button disabled={decide.isPending} onClick={() => decide.mutate({ state: w.state === 'unprotected' ? 'pending' : 'unprotected' })}>
            {w.state === 'unprotected' ? m.protect : m.skip}</Button>
        </div>
      </Panel>
      {decide.error && <ErrorNotice error={decide.error} onRefresh={() => void queryClient.invalidateQueries({ queryKey: ['workspaces'] })} />}
    </Page>
  );
}
